<script lang="ts">
    import { onMount, createEventDispatcher } from "svelte";
    import { navigate } from "svelte-navigator";
    import { loader, swClient, toast } from "../stores";
    import type {
        CreateProductDataIn,
        Meca,
        Product,
        ProductLinkAssetIn,
        SaveStatus,
        UpdateProductDataIn,
        UpdateProductIn,
    } from "../model";
    import ProductImageComponent from "./ProductImageComponent.svelte";

    const dispatch = createEventDispatcher();

    export let product: Product;
    export let productBackup: Product;
    /** document uid : only needed in creation */
    export let documentUid: string = null;

    let datasetList = [
        "brand",
        "description",
        "source",
        "meca",
        "price",
        "priceWithoutDiscount",
        "discountAmount",
        "discountUnit",
        "discountLabel",
        "videos",
        "techDocs",
        "discountedPriceSecondProduct",
        "discountPriceQty",
        "discountedPriceLot",
    ];

    let saveStatus: SaveStatus = "lock";

    let currentDoc;
    let campaign;

    /** product from solidwind */
    let swProduct;
    /** datasets in object from swProduct */
    let datasets;
    /** is onMount finished */
    let isInit: boolean = false;
    /** Dropdown option for child categories*/
    let dropdownChildCategoryOptions: Array<{ name: string; uid: string }> = [];

    /** Value of current sector binded*/
    let sectorBinding: string;
    /** Value of current Meca binded*/
    let currentMeca: Meca;
    /** images at the initialisation of product*/
    let oldImages: string[] = [];
    /** pictos at the initialisation of product*/
    let oldPictos: string[] = [];
    /** pictos at the initialisation of product*/
    let oldLogos: string[] = [];

    let assets: any[] = [];

    let imagesComp;
    let logosComp;
    let pictosComp;

    /**
     * compare datas for save status
     */
    $: if (product) {
        let currentStr = JSON.stringify(product);
        let currentBackupStr = JSON.stringify(productBackup);
        let lock = false;
        if (product) {
            if (!product.name) {
                lock = true;
                console.error(product);
            }
        }
        if (!lock && currentStr === currentBackupStr) {
            saveStatus = "ok";
        } else if (lock) {
            saveStatus = "lock";
        } else {
            saveStatus = "toSave";
        }
    }

    /** Dropdown option for meca*/
    const mecaList: { meca: Meca, label: string, group: 'simple' | 'lot' | '' }[] = [
        {meca: 'meca0', label: 'aucune', group: ''},
        {meca: 'meca1', label: 'REDUCTION IMMEDIATE %', group: 'simple'},
        {meca: 'meca2', label: 'REDUCTION IMMEDIATE €', group: 'simple'},
        {meca: 'meca3', label: '-X% SUR LE Xe PRODUIT', group: 'lot'},
        {meca: 'meca4', label: 'X€ sur la carte de fidélité', group: 'simple'},
    ];

    onMount(async () => {
        const processId = $loader.show("Chargement, merci de patienter");
        await mountProduct();
        _initMecaPromo();
        $loader.hide(processId);
    });

    async function mountProduct() {
        // get product from SW
        await getSwProduct();
        let sectors = await $swClient.get(`/v2/sectors`);
        let tempSectors = []
        for (let sector of sectors) {
            if (!sector.inactive) {
                tempSectors = [...tempSectors, sector]
            }
        }
        sectors = tempSectors
        currentDoc = await $swClient.get(`/v2/documents/${documentUid}`);
        campaign = await $swClient.get(
            `/v2/campaigns/${currentDoc.campaign_uid}`
        );
        // init dropdownChildCategoryOptions
        dropdownChildCategoryOptions = sectors.map((sector) => {
            return {
                name: sector.name,
                uid: sector.uid,
            };
        });
        product.name = swProduct.name;
        // init datasets
        for (let dataset of swProduct.dataset) {
            switch (dataset.name) {
                case "videos":
                case "techDocs":
                    product[dataset.name] = JSON.parse(dataset.value);
                    break;
                default:
                    product[dataset.name] = dataset.value;
                    break;
            }
        }
        if (!product.meca || product.meca === "") {
            product.meca = "meca0";
        }
        if(!product.images) product.images = [];
        if(!product.pictos) product.pictos = [];
        if(!product.logos) product.logos = [];
        oldImages = JSON.parse(JSON.stringify(product.images)) || []; // not keeping reference
        oldPictos = JSON.parse(JSON.stringify(product.pictos)) || []; // not keeping reference
        oldLogos = JSON.parse(JSON.stringify(product.logos)) || []; // not keeping reference

        isInit = true;
        productBackup = JSON.parse(JSON.stringify(product));
    }

    async function getSwProduct() {
        if (product && product.uid) {
            swProduct = await $swClient.get(`/v2/products/${product.uid}`);
            assets = [];
            for (let assetUid of swProduct.assets) {
                const asset = await $swClient.get(`/v2/assets/${assetUid}`);
                assets = [...assets, asset];
            }
        } else {
            (product as any) = {
                images: [],
                pictos: [],
                videos: [],
                techDocs: [],
            };
            swProduct = {
                dataset: [],
                agency: { uid: "" },
                brand: { uid: "" },
                geo_area: { uid: "" },
                sector: { uid: "" },
                name: "",
                master_uid: "",
            };
        }
    }

    /**
     * Convert date format in string
     * @param date date to convert
     */
    function _convertDateForBinding(date: Date): string {
        if (date) {
            let month = "" + (date.getMonth() + 1);
            let day = "" + date.getDate();
            const year = date.getFullYear();

            if (month.length < 2) month = "0" + month;
            if (day.length < 2) day = "0" + day;

            return [year, month, day].join("-");
        } else {
            return "";
        }
    }

    function onMecaChange() {
        product.meca = currentMeca
        product.offertX = '';
        product.offertY = '';
        product.discountPriceQty = '';
        product.priceWithoutDiscount = '';
        product.conditioning = '';
        product.discountedPriceLot = '';
        product.discountUnit = '';
        product.discountAmount = '';
        product.discountedPriceSecondProduct = '';
        switch(product.meca) {
            case "meca1" : {
                product.discountLabel = "REDUCTION DE X%";
                product.discountUnit = "%";
                break;
            }
            case "meca2" : {
                product.discountLabel = "REDUCTION DE X€";
                product.discountUnit = "€";
                break;
            }
            case "meca3" : {
                product.discountPriceQty = '2';
                product.discountLabel = '-X% SUR LE Xe PRODUIT';
                product.discountUnit = "%";
                break;
            }
            case "meca4": {
                product.discountLabel = '-X€ avec la carte de fidélité';
                product.discountUnit = "%";
                break;
            }
        }
    }

    /**
     * Save product with its new values
     */
    async function saveProductAsync() {
        const processId = $loader.show("Sauvegarde, merci de patienter");
        let mode = "";

        try {
            // update product
            const updateProductIn: UpdateProductIn = {
                agency_uid: swProduct.agency_uid,
                brand_uid: swProduct.brand_uid,
                geo_area_uid: swProduct.geo_area_uid,
                master_uid: swProduct.master_uid,
                name: product.name,
                sector_uid: product.categoryChildUid,
            };
            if (product.uid) {
                //update
                mode = "update";
                await $swClient.patch(
                    `/v2/products/${product.uid}`,
                    updateProductIn,
                    null
                );
            } else if (documentUid) {
                mode = "create";
                updateProductIn.geo_area_uid = currentDoc.geo_area.uid;
                updateProductIn.sector_uid = product.categoryChildUid;
                const document = await $swClient.get(
                    `/v2/documents/${documentUid}`
                );
                // puis trouver la 1st page du doc sur lequel on est documentUid
                const firstPage =
                    currentDoc.pages && currentDoc.pages.length > 0
                        ? currentDoc.pages[0]
                        : "";
                if (firstPage && firstPage !== "") {
                    // create product to campaign
                    const newpProduct = await $swClient.post(
                        `/v2/campaigns/${currentDoc.campaign_uid}/products`,
                        updateProductIn,
                        null
                    );
                    product.uid = newpProduct.uid;

                    // set categories document (france en 3) to product
                    for (let i = 0; i < document.categories.length; i++) {
                        const categorie = document.categories[i];
                        await $swClient.put(
                            `/v2/products/${product.uid}/categories/${categorie}`,
                            {},
                            null
                        );
                    }

                    // add product to page
                    await $swClient.put(
                        `/v2/document-pages/${firstPage}/versions/last/products/${product.uid}`,
                        {
                            Hmm: 1,
                            Wmm: 1,
                            Xmm: 0,
                            Ymm: 0,
                        },
                        null
                    );
                } else {
                    throw new Error();
                }
            } else {
                throw new Error();
            }

            // update visuels
            product.images = await _saveImageAsync(oldImages, JSON.parse(JSON.stringify(product.images)), "visuels");
            oldImages = JSON.parse(JSON.stringify(product.images)) || [];

            if(product.pictos) {
                // update pictos
                product.pictos = await _saveImageAsync(oldPictos, JSON.parse(JSON.stringify(product.pictos)), 'pictos');
            }
            if(product.logos) {
                // update logos
                product.logos = await _saveImageAsync(oldLogos, JSON.parse(JSON.stringify(product.logos)), 'logos');
            }


            // update techDocs
            await _saveDocumentAsync();

            // update product's datasets
            let productKeys = Object.keys(product);
            productKeys = [...productKeys, "source"];
            for (let key of productKeys) {
                if (
                    datasetList.includes(key) &&
                    product[key] !== productBackup[key]
                ) {
                    // if key product is dataset and compare product and productBackup key
                    const duid = _findDatasetUid(key);
                    // update if already exists
                    if (duid != "") {
                        if (
                            (product[key] && product[key] != "") ||
                            key === "source"
                        ) {
                            let datasetValue = "";
                            if (key === "source") {
                                datasetValue = "BO";
                            } else {
                                if (typeof product[key] == "object") {
                                    datasetValue = JSON.stringify(product[key]);
                                } else {
                                    datasetValue = product[key].toString();
                                }
                            }
                            // update dataset if value
                            const updateProductDataIn: UpdateProductDataIn = {
                                type: "string",
                                value: datasetValue,
                            };
                            await $swClient.patch(
                                `/v2/products/${product.uid}/data/${duid}`,
                                updateProductDataIn,
                                null
                            );
                        } else {
                            // delete dataset if value is empty
                            if (key !== "source") {
                                // safe for never delete source dataset
                                await $swClient.delete(
                                    `/v2/products/${product.uid}/data/${duid}`,
                                    null,
                                    null
                                );
                            }
                        }
                    } else {
                        // create
                        if (
                            (product[key] && product[key] != "") ||
                            key === "source"
                        ) {
                            let datasetValue = "";
                            if (key === "source") {
                                datasetValue = "BO";
                            } else {
                                if (typeof product[key] == "object") {
                                    datasetValue = JSON.stringify(product[key]);
                                } else {
                                    datasetValue = product[key].toString();
                                }
                            }
                            const createProductDataIn: CreateProductDataIn = {
                                name: key,
                                type: "string",
                                value: datasetValue,
                            };
                            await $swClient.post(
                                `/v2/products/${product.uid}/data`,
                                createProductDataIn,
                                null
                            );
                        }
                    }
                }
            }
            await mountProduct(); // remount the entire product
            imagesComp.refresh();
            logosComp.refresh();
            pictosComp.refresh();
            $loader.hide(processId);
            $toast.success(`Sauvegarde effectuée avec succes`);
            if (mode === "update") {
                // location.reload();
                productBackup = JSON.parse(JSON.stringify(product));
            } else {
                navigate(`/admin/product/${documentUid}/${product.uid}`);
            }
        } catch (err) {
            console.error(err);
            $loader.hide(processId);
            $toast.error(
                `Probleme lors de la mise à jour du produit : ${err.statusText}`,
                10000
            );
        }
    }

    async function _saveDocumentAsync() {
        for (let techDoc of product.techDocs) {
            if (techDoc.url.startsWith("https")) {
            } else {
                //base64 => new image
                const contentTypeRegex = /data:(.+);base64/gm;
                const contentType = contentTypeRegex.exec(techDoc.url);
                const b64 = techDoc.url.split("base64,");
                if (b64[1] && contentType[1]) {
                    // image to base64
                    const body = _base64ToArrayBuffer(b64[1]);
                    const header: Headers = new Headers();
                    header.append("Content-Type", contentType[1]);
                    try {
                        // upload the datafile
                        const datafile = await $swClient.post(
                            `/v2/datafiles`,
                            body,
                            header
                        );
                        techDoc.url =
                            $swClient.uri + "/v2/datafiles/" + datafile.uid;
                    } catch (err) {
                        console.error(err);
                    }
                }
            }
        }
    }

    /**
     * Save the new images of a product
     * @param oldImages array of old images
     * @param newImages array of new images
     * @param type type of images : visues or pictos
     */
    async function _saveImageAsync(oldImages: string[], newImages: string[], type: "visuels" | "pictos" | "logos"): Promise<string[]> {
        let newImagesTemp = JSON.parse(JSON.stringify(newImages)); // set a temp to verify oldImages for ignore the doubles
        for (let i = 0; i < oldImages.length; i++) {
            const oldImage = oldImages[i];
            // check if oldImage is in newImage
            if (newImagesTemp.includes(oldImage)) {
                // ok
                let nbSameImg = newImagesTemp.filter((e) => e == oldImage).length
                    for (let isame = 0;isame < nbSameImg;isame++) {
                        let index = newImagesTemp.indexOf(oldImage);
                        newImagesTemp.splice(index, 1);
                        if (isame > 0) {
                            let index = newImages.indexOf(oldImage);
                            newImages.splice(index, 1);
                        }
                    }
            } else {
                // delete old image
                const urlSplitted = oldImage.split("/"); // => la fin de l'url
                if (urlSplitted && urlSplitted.length > 0) {
                    // get datafile uid
                    const datafileUid = urlSplitted[urlSplitted.length - 1];
                    let assetUid = "";
                    try {
                        // get asset uid
                        // for each asset in swProduct.assets, check if its datafile equal our datafileUid
                        for (let i = 0; i < swProduct.assets?.length; i++) {
                            const productAssetUid = swProduct.assets[i];
                            const asset = await $swClient.get(
                                `/v2/assets/${productAssetUid}`
                            );
                            if (asset.datafile_uid === datafileUid) {
                                assetUid = productAssetUid;
                                break;
                            }
                        }
                        if (assetUid != "") {
                            // delete the asset
                            await $swClient.delete(
                                `/v2/products/${product.uid}/assets/${assetUid}`,
                                null,
                                null
                            );
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }
            }
        }
        for (let i = 0; i < newImages.length; i++) {
            const image: string = newImages[i];

            // if it's an old image, it begins with https (=> link to soliwind)
            if (image.startsWith("https")) {
                // ok
                let oldImgIndex = oldImages.findIndex((e) => e === image);
                if (i !== oldImgIndex) {
                    const productLinkAssetIn: ProductLinkAssetIn = {
                        pos: i + 1,
                    };
                    const urlSplitted = image.split("/"); // => la fin de l'url
                    if (urlSplitted && urlSplitted.length > 0) {
                        // get datafile uid
                        const datafileUid = urlSplitted[urlSplitted.length - 1];
                        const assetUid = assets.find(
                            (e) => e.datafile_uid === datafileUid
                        ).uid;
                        await $swClient.put(
                            `/v2/products/${product.uid}/assets/${assetUid}`,
                            productLinkAssetIn,
                            null
                        );
                    }
                }
            } else {
                //base64 => new image
                const contentTypeRegex = /data:(.+);base64/gm;
                const contentType = contentTypeRegex.exec(image);
                const b64 = image.split("base64,");
                if (b64[1] && contentType[1]) {
                    // image to base64
                    const body = _base64ToArrayBuffer(b64[1]);
                    const header: Headers = new Headers();
                    header.append("Content-Type", contentType[1]);
                    try {
                        // upload the datafile
                        const datafile = await $swClient.post(
                            `/v2/datafiles`,
                            body,
                            header
                        );
                        const productLinkAssetIn: ProductLinkAssetIn = {
                            pos: i,
                        };
                        let createAssetIn = {};
                        if (type === "visuels") {
                            createAssetIn = {
                                class_hint: "",
                                name: product.name,
                                sector_uid: product.categoryChildUid,
                                source_datafile_uid: datafile.uid,
                                type: "packshot",
                            };
                        } else if (type == "pictos") {
                            createAssetIn = {
                                class_hint: "product-element",
                                name: `picto_${product.name}`,
                                sector_uid: product.categoryChildUid,
                                source_datafile_uid: datafile.uid,
                                type: "picto",
                            };
                        } else if(type == "logos") {
                            createAssetIn = {
                                class_hint: "product-element",
                                name: `logo_${product.name}`,
                                sector_uid: product.categoryChildUid,
                                source_datafile_uid: datafile.uid,
                                type: "logo",
                            };
                        }
                        // create the asset
                        const asset = await $swClient.post(
                            `/v2/assets`,
                            createAssetIn,
                            null
                        );
                        // add image to product
                        await $swClient.put(
                            `/v2/products/${product.uid}/assets/${asset.uid}`,
                            productLinkAssetIn,
                            null
                        );
                        newImages[i] = $swClient.uri + "/v2/datafiles/" + datafile.uid
                    } catch (err) {
                        console.error(err);
                    }
                }
            }
        }
        return newImages
    }

    /**
     * Base64 to array buffer
     * @param base64 string in base64
     */
    function _base64ToArrayBuffer(base64: string) {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
     * Find dataset uid from dataset name
     * @param value name of the dataset
     */
    function _findDatasetUid(value: string): string {
        for (let i = 0; i < swProduct.dataset.length; i++) {
            const dataset = swProduct.dataset[i];
            if (dataset.name == value) {
                return dataset.uid;
            }
        }
        return "";
    }

    function imgChanged(event) {
        product.images = event.detail.images;
        dispatch("imgChanged", {
            product: product,
        });
    }

    function logoChanged(event) {
        product.logos = event.detail.images;
        dispatch("imgChanged", {
            product: product,
        });
    }

    function pictoChanged(event) {
        product.pictos = event.detail.images;
        dispatch("imgChanged", {
            product: product,
        });
    }

    function videosChanged(event) {
        product.videos = event.detail.images;
        dispatch("imgChanged", {
            product: product,
        });
        console.log(product.videos)
    }

    function techDocsChanged(event) {
        product.techDocs = event.detail.images;
        dispatch("imgChanged", {
            product: product,
        });
    }

    function beforeUnload() {
        if (saveStatus == "toSave" || saveStatus == "lock") {
            event.preventDefault();
            event.returnValue = "";
            return "";
        }
    }

    /**
     * Init the meca promo
     */
    function _initMecaPromo() {
        if (product.meca) {
            currentMeca = mecaList.find(e => e.meca == product.meca).meca;
        } else {
            currentMeca = mecaList[0].meca;
        }
    }

</script>

<svelte:window on:beforeunload={beforeUnload} />

{#if product !== null && isInit}
    <div class="container">
        <!-- NAME -->
        <div class="form-row">
            <div class="form-label">
                <span>Libellé *</span>
            </div>
            <div
                class="form-input"
                class:has-error={product.name === "" || !product.name}
            >
                <input
                    type="text"
                    Placeholder="Libellé"
                    bind:value={product.name}
                />
            </div>
        </div>
        <!-- DESCRIPTION -->
        <div class="form-row">
            <div class="form-label">
                <span>Description</span>
            </div>
            <div class="form-input">
                <textarea
                    rows="5"
                    cols="33"
                    Placeholder="Description"
                    bind:value={product.description}
                />
            </div>
        </div>
        <!-- BRAND -->
        <div class="form-row">
            <div class="form-label">
                <span>Marque</span>
            </div>
            <div class="form-input">
                <input
                    type="text"
                    Placeholder="Marque"
                    bind:value={product.brand}
                />
            </div>
        </div>

        <!-- CATEGORY -->
        <div class="form-row">
            <div class="form-label">
                <span>Catégorie *</span>
            </div>
            <div class="form-input">
                <select
                    class="mb-3"
                    class:has-error={product.categoryChildUid === "" ||
                        !product.categoryChildUid}
                    name="ChildCat"
                    id="ChildCat"
                    bind:value={product.categoryChildUid}
                >
                    {#each dropdownChildCategoryOptions as option}
                        <option value={option.uid}>{option.name}</option>
                    {/each}
                </select>
            </div>
        </div>

        <!-- MECA -->
        <div class="form-row">
            <div class="form-label">
                <span>Mécanique promo</span>
            </div>
            <div class="form-input">
                <select
                    class="mb-3"
                    class:has-error={product.meca === "" || !product.meca}
                    name="meca"
                    id="meca"
                    bind:value={currentMeca}
                    on:change={onMecaChange}
                >
                    {#each mecaList as option}
                        {#if option.group === ''}
                            <option value="{option.meca}">{option.label}</option>
                        {/if}
                    {/each}
                    <optgroup label="promo simple">
                        {#each mecaList as option}
                            {#if option.group === 'simple'}
                                <option value="{option.meca}">{option.label}</option>
                            {/if}
                        {/each}
                    </optgroup>
                    <optgroup label="promo de lot">
                        {#each mecaList as option}
                            {#if option.group === 'lot'}
                                <option value="{option.meca}">{option.label}</option>
                            {/if}
                        {/each}
                    </optgroup>

                </select>
            </div>
        </div>

        <!-- PRICE -->
        <div class="form-row">
            <div class="form-label">
                <span>
                    Prix
                    {#if product.meca === "meca1" || product.meca === "meca2"}
                        AVEC réduction
                    {/if}
                </span>
            </div>
            <div class="form-input">
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    Placeholder="Prix"
                    bind:value={product.price}
                />
            </div>
        </div>

        {#if product.meca && product.meca !== "" && product.meca !== "meca0"}
            <!-- DISCOUNT -->
            <div class="form-row">
                <div class="form-label">
                    <span
                        >Valeur de la réduction en {product.discountUnit ===
                        "€"
                            ? "€"
                            : "%"}</span
                    >
                </div>
                <div class="form-input">
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        Placeholder="Valeur de la réduction {product.discountUnit ===
                        '€'
                            ? 'euro'
                            : 'pourcentage'}"
                        bind:value={product.discountAmount}
                    />
                </div>
            </div>
        {/if}

        <!-- DISCOUNT PRICE QUANTITY -->
        {#if currentMeca == "meca3"}
            <div class="form-row">
                <div class="form-label">
                        <span>
                            Réduction sur le produit numéro
                        </span>
                </div>
                <div class="form-input">
                    <input type="number" step="1" min="2" Placeholder="réduction sur le Xe produit"
                           bind:value={product.discountPriceQty}>
                </div>
            </div>
        {/if}

        <!-- PRICE PRODUCT Y -->
        {#if currentMeca === 'meca3'}
            <div class="form-row">
                <div class="form-label">
                        <span>
                            {#if parseInt(product.discountPriceQty, 10) > 0}
                                Prix du {product.discountPriceQty}e produit
                                {:else}
                                Prix d'un produit Y
                            {/if}

                        </span>
                </div>
                <div class="form-input">
                    <input type="number" step="0.01" min="0" Placeholder="Prix"
                           bind:value={product.discountedPriceSecondProduct}>
                </div>
            </div>
        {/if}

        <!-- PRICE LOT -->
        {#if currentMeca === 'meca3'}
            <div class="form-row">
                <div class="form-label">
                    <span>Prix du lot</span>
                </div>
                <div class="form-input">
                    <input type="number" step="0.01" min="0" Placeholder="Prix"
                           bind:value={product.discountedPriceLot}>
                </div>
            </div>
        {/if}

        <!-- IMAGES -->
        <div class="form-row">
            <div class="form-label">
                <span>Images</span>
            </div>
            <div class="form-input">
                <ProductImageComponent
                        bind:this={imagesComp}
                    bind:images={product.images}
                    on:imgChanged={(event) => {
                        imgChanged(event);
                    }}
                />
            </div>
        </div>

        <div class="form-row">
            <div class="form-label">
                <span>Pictos</span>
            </div>
            <div class="form-input">
                <ProductImageComponent bind:this={pictosComp} bind:images={product.pictos} on:imgChanged={(event) => {pictoChanged(event);}}
                />
            </div>
        </div>
        <div class="form-row">
            <div class="form-label">
                <span>Logos</span>
            </div>
            <div class="form-input">
                <ProductImageComponent bind:this={logosComp} bind:images={product.logos} on:imgChanged={(event) => {logoChanged(event);}}
                />
            </div>
        </div>

        <!-- VIDEOS -->
        <div class="form-row">
            <div class="form-label">
                <span>Videos</span>
            </div>
            <div class="form-input">
                <ProductImageComponent
                    bind:images={product.videos}
                    type="video"
                    on:imgChanged={(event) => {
                        videosChanged(event);
                    }}
                />
            </div>
        </div>

        <!-- TECHDOCS -->
        <div class="form-row">
            <div class="form-label">
                <span>Documents techniques</span>
            </div>
            <div class="form-input">
                <ProductImageComponent
                    bind:images={product.techDocs}
                    type="document"
                    on:imgChanged={(event) => {
                        techDocsChanged(event);
                    }}
                />
            </div>
        </div>

        <!-- SAVE BUTTON -->
        <div style="float: right;">
            <button
                class="button saveButton btn-elpev {saveStatus}"
                on:click={saveProductAsync}
                title={saveStatus === "ok"
                    ? "Enregistrement ok"
                    : saveStatus === "lock"
                    ? "Enregistrement verrouillé, quelque chose n'est pas correct"
                    : "Enregistrer"}
                disabled={saveStatus == "lock"}
            >
            
                <i class="fas fa-save" style="margin-right: 6px;" /> Enregistrer
            </button>
        </div>
    </div>
{/if}

<style lang="scss">
    @import "../styles/variables";

    .btn-file {
        input[type="file"] {
            display: none;
        }
    }

    .button.save {
        background-color: $dark-black;
        color: white;
    }

    .container {
        margin-top: 15px;
        margin-right: 15px;
        margin-bottom: 15px;
        margin-left: 15px;
        border-right: 1px solid $grey-light;

        .form-row {
            width: 100%;
            display: flex;
            margin-top: 15px;

            .form-label {
                width: 30%;
                font-size: 0.9rem;
                font-weight: bold;
            }

            .form-input {
                width: 70%;

                &.has-error {
                    border-bottom: 1px solid $red !important;
                }

                input,
                select,
                textarea,
                input:focus,
                select:focus,
                textarea:focus,
                input:focus-visible,
                select:focus-visible,
                textarea:focus-visible {
                    width: 100%;
                    border: none !important;
                    border-bottom: 1px solid $dark-black !important;
                    resize: none;
                    background-color: transparent;

                    &.has-error {
                        border-bottom: 1px solid red !important;
                    }
                }

                input:focus,
                select:focus,
                textarea:focus,
                input:focus-visible,
                select:focus-visible,
                textarea:focus-visible {
                    outline: none;
                    border-bottom: 1px solid $dark-black !important;
                }

                #trades {
                    display: flex;

                    .trade-box {
                        display: flex;
                        flex-direction: column;
                        margin: 0 3px;
                        padding: 3px;
                        text-align: center;
                    }
                }
            }
        }
    }
</style>
